// components/charts/PieChart.tsx
import React, { useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

interface PieChartProps {
  title: string;
  description?: string;
  data: any[];
  valueKey: string;
  nameKey: string;
  colors?: string[];
  showLegend?: boolean;
  height?: number;
  loading?: boolean;
  error?: string;
  isDonut?: boolean;
  innerRadius?: number;
  outerRadius?: number;
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  description,
  data,
  valueKey,
  nameKey,
  colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#84cc16",
    "#f97316",
  ],
  showLegend = true,
  height = 400,
  loading = false,
  error,
  isDonut = false,
  innerRadius = 0,
  outerRadius = 120,
}) => {
  const pieInnerRadius = isDonut ? innerRadius || 60 : 0;

  const CustomTooltip = useCallback(({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0];
    return (
      <div className="custom-tooltip">
        <div className="tooltip-item">
          <div className="tooltip-label">
            <div
              className="tooltip-dot"
              style={{ backgroundColor: data.payload.fill }}
            />
            <span>{data.name}</span>
          </div>
          <span className="tooltip-value">
            {typeof data.value === "number"
              ? data.value.toLocaleString()
              : data.value}
          </span>
        </div>
      </div>
    );
  }, []);

  const CustomLegend = useCallback(
    ({ payload }: any) => {
      if (!showLegend) return null;

      return (
        <div className="chart-legend">
          {payload?.map((entry: any, index: number) => (
            <div key={index} className="legend-item">
              <div
                className="legend-dot"
                style={{ backgroundColor: entry.color }}
              />
              <span className="legend-text">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    },
    [showLegend]
  );

  const validatedData = useMemo(() => {
    if (!data?.length) return null;

    const firstItem = data[0];
    if (!(valueKey in firstItem) || !(nameKey in firstItem)) {
      return null;
    }

    return data.filter(
      (item) =>
        item[valueKey] !== undefined &&
        item[nameKey] !== undefined &&
        item[valueKey] > 0
    );
  }, [data, valueKey, nameKey]);

  const chartElements = useMemo(() => {
    if (!validatedData?.length) return null;

    return (
      <>
        <Pie
          data={validatedData}
          cx="50%"
          cy="50%"
          innerRadius={pieInnerRadius}
          outerRadius={outerRadius}
          paddingAngle={2}
          dataKey={valueKey}
          nameKey={nameKey}>
          {validatedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>

        <Tooltip content={<CustomTooltip />} />

        {showLegend && <Legend content={<CustomLegend />} />}
      </>
    );
  }, [
    validatedData,
    pieInnerRadius,
    outerRadius,
    valueKey,
    nameKey,
    colors,
    showLegend,
    CustomTooltip,
    CustomLegend,
  ]);

  const chartStates = useMemo(
    () => ({
      loading: (
        <div className="chart-loading">
          <i className="ti ti-loader-2 spin" />
          <span>Loading...</span>
        </div>
      ),
      error: (
        <div className="chart-error">
          <i className="ti ti-alert-circle" />
          <span>{error}</span>
        </div>
      ),
      empty: (
        <div className="chart-empty">
          <i className="ti ti-chart-pie" />
          <span>No data available</span>
        </div>
      ),
      invalidData: (
        <div className="chart-error">
          <i className="ti ti-alert-triangle" />
          <span>Invalid data format</span>
          <small>
            Required fields: {valueKey}, {nameKey}
          </small>
        </div>
      ),
    }),
    [error, valueKey, nameKey]
  );

  const renderChart = () => {
    if (loading) return chartStates.loading;
    if (error) return chartStates.error;
    if (!data?.length) return chartStates.empty;
    if (!validatedData?.length) return chartStates.invalidData;

    return <RechartsPieChart>{chartElements}</RechartsPieChart>;
  };

  return (
    <div className="pie-chart-container">
      <div className="chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PieChart;
